import React, { useEffect } from 'react';
import ZoomControls, { ZOOM_VALUES } from 'packages/common/ZoomControls/ZoomControls';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import { useIsModalOpen } from 'packages/hooks/useIsModalOpen';

const CanvasZoomControls = ({ renderer }) => {
    const isModalOpen = useIsModalOpen();
    const forceUpdate = useForceUpdate();

    const getZoomPercent = () => Math.ceil((renderer?.getScale() || 1) * 100);

    const getPrevZoomValue = () => {
        const zoomPercent = getZoomPercent();
        const defaultArrayIdx = ZOOM_VALUES.indexOf(zoomPercent);

        if (defaultArrayIdx === -1) {
            const closestBiggerValueIdx = ZOOM_VALUES.findIndex((currentValue) => currentValue > zoomPercent);

            return closestBiggerValueIdx === -1
                ? ZOOM_VALUES[ZOOM_VALUES.length - 1]
                : ZOOM_VALUES[closestBiggerValueIdx - 1];
        }

        if (defaultArrayIdx === 0) {
            return null;
        }

        return ZOOM_VALUES[defaultArrayIdx - 1];
    };

    const getNextZoomValue = () => {
        const zoomPercent = getZoomPercent();
        const defaultArrayIdx = ZOOM_VALUES.indexOf(zoomPercent);

        if (defaultArrayIdx === -1) {
            const closestBiggerValueIdx = ZOOM_VALUES.findIndex((currentValue) => currentValue > zoomPercent);

            return closestBiggerValueIdx === -1
                ? ZOOM_VALUES[ZOOM_VALUES.length - 1]
                : ZOOM_VALUES[closestBiggerValueIdx];
        }

        if (defaultArrayIdx === ZOOM_VALUES.length - 1) {
            return null;
        }

        return ZOOM_VALUES[defaultArrayIdx + 1];
    };

    const setZoomLevel = (zoomValue: number) => {
        if (!renderer) {
            return;
        }

        renderer.setScale(zoomValue / 100);
    };

    const onZoomClick = (value: number) => {
        setZoomLevel(value);
    };

    const zoomOut = () => {
        const val = getPrevZoomValue();

        if (val) {
            setZoomLevel(val);
        }
    };

    const zoomIn = () => {
        const val = getNextZoomValue();

        if (val) {
            setZoomLevel(val);
        }
    };

    const zoomToFit = () => {
        if (!renderer) {
            return;
        }

        renderer.zoomToFit();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!isCtrlKey(event)) {
            return;
        }

        switch (event.code) {
            case 'Equal': {
                event.preventDefault();
                zoomIn();
                break;
            }
            case 'Minus': {
                event.preventDefault();
                zoomOut();
                break;
            }
            case 'Digit0': {
                event.preventDefault();
                setZoomLevel(100);
                break;
            }
            case 'Digit1': {
                event.preventDefault();
                zoomToFit();
                break;
            }

            default: {
                break;
            }
        }
    };

    useEffect(() => {
        if (!renderer || isModalOpen) {
            return;
        }

        document.addEventListener('keydown', handleKeyDown);
        renderer.eventEmitter.on('scaleUpdated', forceUpdate);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            renderer.eventEmitter.off('scaleUpdated', forceUpdate);
        };
    }, [renderer, forceUpdate, isModalOpen]);

    return (
        <ZoomControls
            onZoomOut={zoomOut}
            onZoomIn={zoomIn}
            onZoomClick={onZoomClick}
            onZoomToFit={zoomToFit}
            zoomPercentage={getZoomPercent()}
            maxZoom={ZOOM_VALUES[ZOOM_VALUES.length - 1]}
            minZoom={ZOOM_VALUES[0]}
        />
    );
};

export default CanvasZoomControls;
